import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Detects when the player scrolls out of viewport and manages pause state.
 *
 * Uses Intersection Observer to track visibility. When the element scrolls
 * mostly out of view (< 30% visible), pauses playback if playing. When scrolled
 * back into view, shows an overlay for the user to resume (no auto-play).
 *
 * This is different from tab visibility (handled by visibilitychange event).
 * Scroll visibility handles iframe embeds in scrollable parent pages.
 */
export function useScrollVisibility(
  containerRef: React.RefObject<HTMLElement | null>,
  isPlaying: boolean,
  onPause: () => void
) {
  const [pausedByScroll, setPausedByScroll] = useState(false);
  const wasPlayingBeforeScrollRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          // Scrolled out of view — pause if currently playing
          if (isPlaying) {
            wasPlayingBeforeScrollRef.current = true;
            onPause();
            setPausedByScroll(true);
          }
        } else {
          // Scrolled back into view
          // If we previously paused due to scroll-out, show the overlay
          // (but don't auto-resume — let user click to resume)
          if (wasPlayingBeforeScrollRef.current && !isPlaying) {
            setPausedByScroll(true);
          }
        }
      },
      {
        // Trigger when less than 30% of the player is visible
        threshold: 0.3,
      }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, isPlaying, onPause]);

  /**
   * Call this when user clicks resume button.
   * Clears the scroll-pause state so overlay hides.
   */
  const handleScrollResume = useCallback(() => {
    wasPlayingBeforeScrollRef.current = false;
    setPausedByScroll(false);
  }, []);

  /**
   * Call this when video starts playing through other means (e.g., controls).
   * Clears the scroll-pause state so overlay hides.
   */
  const clearScrollPause = useCallback(() => {
    wasPlayingBeforeScrollRef.current = false;
    setPausedByScroll(false);
  }, []);

  return {
    /** True when we should show the scroll-pause overlay */
    pausedByScroll,
    /** Call when user clicks resume on the overlay */
    handleScrollResume,
    /** Call to clear scroll-pause state (e.g., when playing via controls) */
    clearScrollPause,
  };
}
